import { Controller } from "@hotwired/stimulus"

const MAX_PRESTACIONES = 3
const MAX_GLOBAL_PAYMENT_LINES = 3

const ZERO_AMOUNT_EPSILON = 0.005

const ALERT_MESSAGES = {
  noSelection: "Debe seleccionar al menos una prefactura para cobrar",
  maxPrestaciones: `Máximo ${MAX_PRESTACIONES} prestaciones por comprobante (triplicado en A4). Genere otro comprobante para el resto.`,
  missingGlobalLines: "Debe cargar al menos una línea de pago para el comprobante",
  linesRange: `El comprobante permite entre 1 y ${MAX_GLOBAL_PAYMENT_LINES} líneas de pago`,
  invalidLine: "Complete método y monto válido en todas las líneas de pago",
  duplicateMethods: "No puede repetir métodos de pago en el mismo comprobante",
  totalMismatch: "La suma de líneas de pago debe coincidir con el total a cobrar",
  partialAmountPositive: "El monto del pago parcial debe ser mayor a 0",
  partialAmountExceeds: "El monto del pago parcial no puede superar el monto pendiente de la prefactura",
  creditAmountPositive: "El saldo aplicado debe ser mayor a 0",
  creditAmountExceedsAvailable: "El saldo aplicado no puede superar el saldo a favor disponible",
  creditAmountExceedsTotal: "El saldo aplicado no puede superar el total seleccionado"
}

export default class extends Controller {
  static targets = [
    "amount",
    "displayAmount",
    "finalTotal",
    "form",
    "selectedCount",
    "totalAmount",
    "preInvoiceCheckbox",
    "globalPaymentLines",
    "paymentMethodsSection",
    "zeroChargeNotice",
    "anularFormContainer",
    "anularPreInvoiceId",
    "creditSection",
    "applyCreditCheckbox",
    "appliedCreditWrapper",
    "appliedCreditAmount"
  ]

  connect() {
    this.preInvoiceAmounts = this.extractPreInvoiceAmounts()
    this.syncAllPartialAmountWrappers()
    this.calculate()
  }

  togglePartialPayment(event) {
    const preInvoiceId = event.target.dataset.preInvoiceId
    if (preInvoiceId) {
      this.syncPartialAmountWrapperVisibility(preInvoiceId)
    }
    this.calculate()
  }

  toggleCreditApplication() {
    if (this.hasAppliedCreditWrapperTarget && this.hasApplyCreditCheckboxTarget) {
      this.appliedCreditWrapperTarget.classList.toggle("hidden", !this.applyCreditCheckboxTarget.checked)
    }
    this.calculate()
  }

  extractPreInvoiceAmounts() {
    const amounts = {}
    this.preInvoiceCheckboxTargets?.forEach((checkbox) => {
      const preInvoiceId = checkbox.value
      const amount = parseFloat(checkbox.dataset.amount) || 0
      amounts[`pre_invoice_${preInvoiceId}`] = amount
    })

    return amounts
  }

  updateSelectedInvoices(event) {
    const selected = this.getSelectedPreInvoiceCheckboxes()
    if (event?.target?.type === "checkbox" && selected.length > MAX_PRESTACIONES) {
      event.target.checked = false
      alert(ALERT_MESSAGES.maxPrestaciones)
    }

    this.calculate()
  }

  handleSubmit(event) {
    if (this.formTarget.dataset.previewConfirmed === "1") {
      delete this.formTarget.dataset.previewConfirmed
      return
    }

    if (this.formTarget.dataset.comprobanteProcessing === "1") {
      event.preventDefault()
      return false
    }

    event.preventDefault()
    if (!this.validateSelection()) return false
    if (!this.validatePartialAmountsForSelection()) return false
    if (!this.validateAppliedCredit()) return false
    if (!this.validateGlobalPaymentLines()) return false
    this.showPreview()
    return false
  }

  validateSelection() {
    if (!this.hasSelectedCountTarget || !this.hasTotalAmountTarget) return true

    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()
    if (selectedPreInvoices.length === 0) {
      alert(ALERT_MESSAGES.noSelection)
      return false
    }

    if (selectedPreInvoices.length > MAX_PRESTACIONES) {
      alert(ALERT_MESSAGES.maxPrestaciones)
      return false
    }

    return true
  }

  validatePartialAmountsForSelection() {
    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()

    for (const checkbox of selectedPreInvoices) {
      const preInvoiceId = checkbox.value
      if (!this.isPartialPayment(preInvoiceId)) continue

      const cap = parseFloat(checkbox.dataset.amount) || 0
      if (cap <= ZERO_AMOUNT_EPSILON) continue

      const amt = this.partialAmountFieldValue(preInvoiceId)

      if (amt <= 0) {
        alert(ALERT_MESSAGES.partialAmountPositive)
        return false
      }

      if (amt > cap + 0.01) {
        alert(`${ALERT_MESSAGES.partialAmountExceeds} (${this.formatCurrency(cap)})`)
        return false
      }
    }

    return true
  }

  validateGlobalPaymentLines() {
    if (!this.hasGlobalPaymentLinesTarget) return true

    const expected = this.computeExpectedCobroTotal()
    const selectedCount = this.getSelectedPreInvoiceCheckboxes().length
    if (selectedCount > 0 && expected <= ZERO_AMOUNT_EPSILON) return true

    const rows = this.getGlobalPaymentLineRows()
    if (rows.length === 0) {
      alert(ALERT_MESSAGES.missingGlobalLines)
      return false
    }

    if (rows.length > MAX_GLOBAL_PAYMENT_LINES) {
      alert(ALERT_MESSAGES.linesRange)
      return false
    }

    const methods = []
    let lineTotal = 0
    for (const row of rows) {
      const methodInput = row.querySelector("select")
      const amountInput = row.querySelector("input[name*='[amount]']")
      const method = methodInput?.value
      const amount = parseFloat(amountInput?.value) || 0
      if (!method || amount <= 0) {
        alert(ALERT_MESSAGES.invalidLine)
        return false
      }

      methods.push(method)
      lineTotal += amount
    }

    if (new Set(methods).size !== methods.length) {
      alert(ALERT_MESSAGES.duplicateMethods)
      return false
    }

    if (Math.abs(lineTotal - expected) > 0.02) {
      alert(`${ALERT_MESSAGES.totalMismatch} (${this.formatCurrency(expected)})`)
      return false
    }

    return true
  }

  validateAppliedCredit() {
    if (!this.creditApplicationEnabled()) return true

    const amount = this.getAppliedCreditAmount()
    if (amount <= 0) {
      alert(ALERT_MESSAGES.creditAmountPositive)
      return false
    }

    const available = this.getAvailableCredit()
    if (amount > available + 0.01) {
      alert(`${ALERT_MESSAGES.creditAmountExceedsAvailable} (${this.formatCurrency(available)})`)
      return false
    }

    const grossTotal = this.computeSelectedPreInvoicesTotal()
    if (amount > grossTotal + 0.01) {
      alert(`${ALERT_MESSAGES.creditAmountExceedsTotal} (${this.formatCurrency(grossTotal)})`)
      return false
    }

    return true
  }

  isZeroChargeTotal(amount) {
    return amount <= ZERO_AMOUNT_EPSILON
  }

  isZeroChargeSelection(selectedCount, totalAmount) {
    return selectedCount > 0 && this.isZeroChargeTotal(totalAmount)
  }

  computeExpectedCobroTotal() {
    return Math.max(this.computeSelectedPreInvoicesTotal() - this.getAppliedCreditAmount(), 0)
  }

  computeSelectedPreInvoicesTotal() {
    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()

    return selectedPreInvoices.reduce((total, checkbox) => {
      return total + this.getPreInvoiceAmount(checkbox.value)
    }, 0)
  }

  showPreview() {
    const event = new CustomEvent("show-invoice-preview", {
      detail: { form: this.formTarget }
    })
    document.dispatchEvent(event)
  }

  getSelectedPreInvoiceCheckboxes() {
    if (!this.hasPreInvoiceCheckboxTarget) return []
    return this.preInvoiceCheckboxTargets.filter((checkbox) => checkbox.checked)
  }

  isPartialPayment(preInvoiceId) {
    const selector = `input[name="transaction[pre_invoice_payment_lines][${preInvoiceId}][payment_type]"][value="partial"]`
    const partialRadio = this.element.querySelector(selector)
    return partialRadio && partialRadio.checked
  }

  partialAmountFieldValue(preInvoiceId) {
    const inp = this.element.querySelector(
      `input[name="transaction[pre_invoice_payment_lines][${preInvoiceId}][partial_amount]"]`
    )
    return parseFloat(inp?.value) || 0
  }

  getGlobalPaymentLineRows() {
    if (!this.hasGlobalPaymentLinesTarget) return []
    return Array.from(this.globalPaymentLinesTarget.querySelectorAll(".mixed-payment-line"))
  }

  calculate() {
    if (this.hasSelectedCountTarget && this.hasTotalAmountTarget) {
      this.calculateMultiple()
    } else if (this.hasAmountTarget) {
      this.calculateSingle()
    }
  }

  calculateMultiple() {
    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()
    const selectedCount = selectedPreInvoices.length

    const totalAmount = this.computeSelectedPreInvoicesTotal()
    const finalTotal = this.computeExpectedCobroTotal()

    this.updateSummaryDisplay(selectedCount, totalAmount, finalTotal)
  }

  getPreInvoiceAmount(preInvoiceId) {
    if (this.isPartialPayment(preInvoiceId)) return this.partialAmountFieldValue(preInvoiceId)
    return this.preInvoiceAmounts[`pre_invoice_${preInvoiceId}`] || 0
  }

  updateSummaryDisplay(selectedCount, totalAmount, finalTotal) {
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = selectedCount
    }

    if (this.hasTotalAmountTarget) {
      this.totalAmountTarget.textContent = this.formatCurrency(totalAmount)
    }

    if (this.hasFinalTotalTarget) {
      this.finalTotalTarget.textContent = this.formatCurrency(finalTotal)
    }

    this.toggleZeroChargeUi(selectedCount, totalAmount)
    this.toggleAnularButton()
  }

  toggleZeroChargeUi(selectedCount, totalAmount) {
    const zeroChargeOnly = this.isZeroChargeSelection(selectedCount, totalAmount)

    if (this.hasPaymentMethodsSectionTarget) {
      this.paymentMethodsSectionTarget.classList.toggle("hidden", zeroChargeOnly)
    }

    if (this.hasZeroChargeNoticeTarget) {
      this.zeroChargeNoticeTarget.classList.toggle("hidden", !zeroChargeOnly)
    }
  }

  creditApplicationEnabled() {
    return this.hasApplyCreditCheckboxTarget && this.applyCreditCheckboxTarget.checked
  }

  getAvailableCredit() {
    if (!this.hasCreditSectionTarget) return 0
    return parseFloat(this.creditSectionTarget.dataset.availableCredit) || 0
  }

  getAppliedCreditAmount() {
    if (!this.creditApplicationEnabled()) return 0
    const input = this.element.querySelector('input[name="transaction[applied_credit_amount]"]') || this.appliedCreditAmountTarget
    return parseFloat(input?.value) || 0
  }

  toggleAnularButton() {
    if (!this.hasAnularFormContainerTarget) return

    const selectedPreInvoices = this.getSelectedPreInvoiceCheckboxes()
    const exactlyOnePrestation = selectedPreInvoices.length === 1
    const selectedIsPendingOnly = exactlyOnePrestation && selectedPreInvoices[0].dataset.pendingOnly === "true"
    const showAnular = exactlyOnePrestation && selectedIsPendingOnly
    this.anularFormContainerTarget.classList.toggle("hidden", !showAnular)
    if (showAnular && this.hasAnularPreInvoiceIdTarget) {
      this.anularPreInvoiceIdTarget.value = selectedPreInvoices[0].value
    }
  }

  submitAnular(event) {
    event.preventDefault()
    if (!confirm("¿Está seguro que desea solicitar la anulación de esta prestación? Se enviará una notificación al profesor para su autorización.")) {
      return
    }
    const anularForm = document.getElementById("anular_pre_invoice_form")
    if (anularForm) anularForm.requestSubmit()
  }

  calculateSingle() {
    const amount = parseFloat(this.amountTarget.value) || 0
    if (this.hasDisplayAmountTarget) {
      this.displayAmountTarget.textContent = this.formatCurrency(amount)
    }

    if (this.hasFinalTotalTarget) {
      this.finalTotalTarget.textContent = this.formatCurrency(amount)
    }
  }

  formatCurrency(value) {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
      minimumFractionDigits: 2
    }).format(value)
  }

  syncAllPartialAmountWrappers() {
    const ids = new Set()
    this.preInvoiceCheckboxTargets?.forEach((cb) => ids.add(cb.value))
    ids.forEach((id) => this.syncPartialAmountWrapperVisibility(id))
  }

  syncPartialAmountWrapperVisibility(preInvoiceId) {
    const wrap = this.element.querySelector(`[data-partial-amount-wrapper="${preInvoiceId}"]`)
    if (!wrap) return
    wrap.classList.toggle("hidden", !this.isPartialPayment(preInvoiceId))
  }
}
